import { PAGE_SIZE } from "../assets/instateamMasterConfig";
import { AppIcon, MetricCard } from "../components/InstateamMasterComponents";
import {
  currencyFormatter as adminCurrencyFormatter,
  getInitials,
  getPresenceStatusMeta,
  resolveEmployeeProfileStatus,
  toAbsoluteUrl,
} from "../utils/adminHelpers";

export default function EmployeesPage({
  employees,
  totalEmployees,
  activeEmployees,
  inactiveEmployees,
  newJoinings,
  departmentFilter,
  departmentOptions,
  onDepartmentFilterChange,
  onAddEmployee,
  onViewEmployee,
  onExport,
  payrollValue,
  currentPage,
  totalPages,
  onPageChange,
  isLoadingEmployees,
  resultsCount,
  currencyFormatter = adminCurrencyFormatter,
}) {
  return (
    <section className="employees-page">
      <div className="metrics-grid">
        <MetricCard label="Total Employees" value={totalEmployees} helper="All time" icon="employees" />
        <MetricCard
          label="Active Employees"
          value={activeEmployees}
          helper={`${totalEmployees ? Math.round((activeEmployees / totalEmployees) * 100) : 0}% of total`}
          icon="activeUsers"
          tone="success"
        />
        <MetricCard
          label="Inactive Employees"
          value={inactiveEmployees}
          helper={`${totalEmployees ? Math.round((inactiveEmployees / Math.max(totalEmployees, 1)) * 100) : 0}% of total`}
          icon="inactiveUsers"
          tone="danger"
        />
        <MetricCard
          label="Monthly Payroll"
          value={currencyFormatter.format(payrollValue)}
          helper={`${newJoinings} new joinings this month`}
          icon="payroll"
        />
      </div>

      <div className="table-card directory-shell">
        <div className="table-card-toolbar">
          <select
            value={departmentFilter}
            onChange={(event) => onDepartmentFilterChange(event.target.value)}
            className="toolbar-select"
          >
            <option value="all">All Departments</option>
            {departmentOptions.map((department) => (
              <option key={department} value={department}>
                {department}
              </option>
            ))}
          </select>

          <div className="toolbar-actions">
            <button className="secondary-button export-button" type="button" onClick={onExport}>
              <AppIcon name="export" />
              Export
            </button>

            <button className="primary-button add-button" type="button" onClick={onAddEmployee}>
              <AppIcon name="plus" />
              Add Employee
            </button>
          </div>
        </div>

        <div className="directory-summary-bar">
          <div>
            <strong>{resultsCount}</strong>
            <span>employees found</span>
          </div>
          <p>Open any employee row to view the full profile, update details, manage documents, and control access.</p>
        </div>

        {isLoadingEmployees ? (
          <div className="directory-empty-state">Loading employees...</div>
        ) : null}

        {!isLoadingEmployees && employees.length === 0 ? (
          <div className="directory-empty-state">
            No employees match the current search. Try changing the filter or add a new employee.
          </div>
        ) : null}

        {!isLoadingEmployees && employees.length ? (
          <div className="employee-row-list">
            <div className="employee-list-header">
              <span>EMP ID</span>
              <span>EMPLOYEE</span>
              <span>DESIGNATION</span>
              <span>DEPARTMENT</span>
              <span>JOINING DATE</span>
              <span>SALARY</span>
              <span>DOCS</span>
              <span>STATUS</span>
              <span />
            </div>

            {employees.map((employee) => {
              const documentCount = Object.values(employee.documents || {}).filter(
                (file) => file?.url
              ).length;
              const presenceMeta = getPresenceStatusMeta(
                resolveEmployeeProfileStatus(employee)
              );

              return (
                <button
                  key={employee._id}
                  className={`employee-list-row ${employee.status === "inactive" ? "inactive" : ""}`}
                  type="button"
                  onClick={() => onViewEmployee(employee)}
                >
                  <span className="employee-list-cell employee-list-id">{employee.employeeId}</span>

                  <div className="employee-list-cell employee-list-person">
                    <div className="employee-cell">
                      <div className="employee-presence-avatar-wrap compact">
                        {employee.profilePhoto?.url ? (
                          <img
                            src={toAbsoluteUrl(employee.profilePhoto.url)}
                            alt={employee.fullName}
                            className="table-avatar"
                          />
                        ) : (
                          <div className="avatar-circle table-avatar">
                            {getInitials(employee.fullName)}
                          </div>
                        )}
                        <span
                          className={`employee-presence-badge compact ${presenceMeta.className}`}
                          title={presenceMeta.label}
                          aria-label={presenceMeta.label}
                        >
                          {presenceMeta.icon ? (
                            <AppIcon name={presenceMeta.icon} />
                          ) : (
                            <span className="employee-presence-dot" />
                          )}
                        </span>
                      </div>

                      <div className="employee-cell-copy">
                        <strong>{employee.fullName}</strong>
                        <span>
                           {employee.email}
                        </span>
                      </div>
                    </div>
                  </div>

                  <span className="employee-list-cell">{employee.designation}</span>
                  <span className="employee-list-cell">{employee.department}</span>
                  <span className="employee-list-cell">{employee.dateOfJoining || "—"}</span>
                  <span className="employee-list-cell">
                    {currencyFormatter.format(Number(employee.basicSalary || 0))}
                  </span>
                  <span className="employee-list-cell">{documentCount}</span>
                  <div className="employee-list-cell">
                    <span className={`status-badge ${employee.status === "active" ? "active" : "inactive"}`}>
                      {employee.status === "active" ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="employee-list-arrow">
                    <AppIcon name="chevronRight" />
                  </div>
                </button>
              );
            })}
          </div>
        ) : null}

        <div className="table-footer">
          <span>
            Showing {resultsCount ? (currentPage - 1) * PAGE_SIZE + 1 : 0} to{" "}
            {Math.min(currentPage * PAGE_SIZE, resultsCount)} of {resultsCount} entries
          </span>

          <div className="pagination">
            <button
              className="pagination-arrow"
              type="button"
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              <AppIcon name="chevronLeft" />
            </button>

            {Array.from({ length: totalPages }, (_, index) => index + 1)
              .slice(0, Math.min(totalPages, 5))
              .map((page) => (
                <button
                  key={page}
                  className={`page-chip ${page === currentPage ? "active" : ""}`}
                  type="button"
                  onClick={() => onPageChange(page)}
                >
                  {page}
                </button>
              ))}

            <button
              className="pagination-arrow"
              type="button"
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              <AppIcon name="chevronRight" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
